import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Avatar,
  Divider,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useTranslation } from "hooks/useTranslation";
import { EXAM_STATUS_LABEL_KEYS } from "constants/examStatus";
import { reassignExam } from "services/sampleService";

const selectLabelSx = {
  backgroundColor: "background.paper",
  px: 1,
  transform: "translate(14px, -9px) scale(0.75)",
};

const selectSx = {
  height: 56,
  backgroundColor: "#fff",
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    paddingTop: "14px",
    paddingBottom: "14px",
  },
};

function translateExamStatusLabel(status, t) {
  if (!status) return "";
  const key = EXAM_STATUS_LABEL_KEYS[status];
  return key ? t(key, status) : status;
}

function isSlotReassignable(annotator) {
  if (!annotator?.status) return true;
  return annotator.status === "Pending" || annotator.status === "In Progress";
}

function getStatusColor(status) {
  switch (status) {
    case "Pending":
      return "warning";
    case "In Progress":
      return "info";
    case "Submitted":
    case "Annotated Twice":
      return "success";
    case "Annotated Once":
      return "primary";
    default:
      return "default";
  }
}

function SlotSection({
  slotKey,
  label,
  slotType,
  annotator,
  value,
  onChange,
  annotators,
  otherAnnotatorId,
  t,
}) {
  const reassignable = isSlotReassignable(annotator);
  const isAssigned = Boolean(annotator?.id);
  const currentName = isAssigned
    ? annotator.name || `ID: ${annotator.id}`
    : t("exams.table.notAssigned", "Not Assigned");
  const selectLabel = t("exams.reassign.newAnnotator", "New Annotator");
  const placeholder = t("exams.reassign.selectPlaceholder", "Select an annotator");

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        mb: 2,
        backgroundColor: "#fff",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: slotType === "A1" ? "primary.main" : "secondary.main",
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          {slotType}
        </Avatar>
        <MDTypography variant="button" fontWeight="medium">
          {label}
        </MDTypography>
      </Box>

      <Box
        sx={{
          mb: reassignable ? 2 : 0,
          px: 1.5,
          py: 1,
          borderRadius: 1,
          backgroundColor: "#f5f7fa",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.5, textTransform: "uppercase", letterSpacing: 0.4 }}
        >
          {t("exams.reassign.currentAnnotator", "Current Annotator")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: isAssigned ? 500 : 400,
              fontStyle: isAssigned ? "normal" : "italic",
              color: isAssigned ? "text.primary" : "text.secondary",
            }}
          >
            {currentName}
          </Typography>
          {annotator?.status ? (
            <Chip
              label={translateExamStatusLabel(annotator.status, t)}
              size="small"
              color={getStatusColor(annotator.status)}
            />
          ) : null}
        </Box>
      </Box>

      {!reassignable ? (
        <FormHelperText sx={{ mx: 0, mt: 1 }}>
          {t("exams.reassign.slotLocked", "This slot is locked (annotation completed)")}
        </FormHelperText>
      ) : (
        <FormControl fullWidth size="small">
          <InputLabel id={`${slotKey}-new-label`} shrink sx={selectLabelSx}>
            {selectLabel}
          </InputLabel>
          <Select
            labelId={`${slotKey}-new-label`}
            value={value}
            displayEmpty
            label={selectLabel}
            onChange={(e) => onChange(e.target.value)}
            sx={selectSx}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    {placeholder}
                  </Typography>
                );
              }
              const match = annotators.find((a) => String(a.id) === String(selected));
              return match?.name || selected;
            }}
          >
            <MenuItem value="">
              <em>{t("exams.table.none", "None")}</em>
            </MenuItem>
            {annotators.map((a) => (
              <MenuItem
                key={a.id}
                value={a.id}
                disabled={otherAnnotatorId != null && String(otherAnnotatorId) === String(a.id)}
              >
                {a.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {t(
              "exams.reassign.selectHelper",
              "Choose an annotator to assign or replace the current one."
            )}
          </FormHelperText>
        </FormControl>
      )}
    </Box>
  );
}

SlotSection.propTypes = {
  slotKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  slotType: PropTypes.oneOf(["A1", "A2"]).isRequired,
  annotator: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    status: PropTypes.string,
  }),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  annotators: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
    })
  ).isRequired,
  otherAnnotatorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  t: PropTypes.func.isRequired,
};

export default function ReassignAnnotatorModal({
  open,
  onClose,
  exam,
  annotators,
  userInfo,
  onSuccess,
  onError,
}) {
  const { t } = useTranslation();
  const [newA1, setNewA1] = useState("");
  const [newA2, setNewA2] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !exam) return;
    setNewA1("");
    setNewA2("");
  }, [open, exam]);

  const changes = useMemo(() => {
    if (!exam) return [];

    const pending = [];

    if (
      isSlotReassignable(exam.annotator1) &&
      newA1 &&
      String(newA1) !== String(exam.annotator1?.id || "")
    ) {
      pending.push({ slot: "annotator1", new_annotator_id: Number(newA1) });
    }

    if (
      isSlotReassignable(exam.annotator2) &&
      newA2 &&
      String(newA2) !== String(exam.annotator2?.id || "")
    ) {
      pending.push({ slot: "annotator2", new_annotator_id: Number(newA2) });
    }

    return pending;
  }, [exam, newA1, newA2]);

  const handleConfirm = async () => {
    if (!exam) return;

    if (changes.length === 0) {
      onError?.({
        color: "warning",
        title: t("exams.reassign.noChange", "No changes detected"),
        message: t("exams.reassign.noChange", "No changes detected"),
      });
      return;
    }

    const selectedA1 = newA1 || exam.annotator1?.id;
    const selectedA2 = newA2 || exam.annotator2?.id;
    if (selectedA1 && selectedA2 && String(selectedA1) === String(selectedA2)) {
      onError?.({
        color: "error",
        title: t("exams.notifications.assignmentFailed", "Assignment Failed"),
        message: t(
          "exams.notifications.sameAnnotator",
          "Annotator 1 and Annotator 2 cannot be the same."
        ),
      });
      return;
    }

    try {
      setLoading(true);

      for (const change of changes) {
        await reassignExam({
          exam_id: exam._row_id,
          slot: change.slot,
          new_annotator_id: change.new_annotator_id,
          assigned_by: userInfo?.id,
        });
      }

      onSuccess?.({
        color: "success",
        title: t("exams.reassign.reassignSuccess", "Annotator reassigned successfully"),
        message: t("exams.reassign.reassignSuccess", "Annotator reassigned successfully"),
      });
      onClose();
    } catch (err) {
      onError?.({
        color: "error",
        title: t("exams.reassign.reassignFailed", "Failed to reassign annotator"),
        message: err?.message || t("exams.reassign.reassignFailed", "Failed to reassign annotator"),
      });
    } finally {
      setLoading(false);
    }
  };

  if (!exam) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SwapHorizIcon color="primary" fontSize="small" />
          <MDTypography variant="h6">
            {t("exams.reassign.modalTitle", "Reassign Annotators")}
          </MDTypography>
        </Box>
        <IconButton onClick={onClose} aria-label="close" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: "#f8f9fa" }}>
        <Box
          sx={{
            mb: 2.5,
            px: 2,
            py: 1.25,
            borderRadius: 1.5,
            backgroundColor: "#fff",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
            {t("exams.table.examId", "Session ID")}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
            {exam.exam_id}
          </Typography>
        </Box>

        <SlotSection
          slotKey="annotator1"
          slotType="A1"
          label={t("exams.assignment.annotator1", "Annotator 1")}
          annotator={exam.annotator1}
          value={newA1}
          onChange={setNewA1}
          annotators={annotators}
          otherAnnotatorId={newA2 || exam.annotator2?.id}
          t={t}
        />

        <SlotSection
          slotKey="annotator2"
          slotType="A2"
          label={t("exams.assignment.annotator2", "Annotator 2")}
          annotator={exam.annotator2}
          value={newA2}
          onChange={setNewA2}
          annotators={annotators}
          otherAnnotatorId={newA1 || exam.annotator1?.id}
          t={t}
        />
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          {t("exams.reassign.cancel", "Cancel")}
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          onClick={handleConfirm}
          disabled={loading || changes.length === 0}
        >
          {loading
            ? t("exams.buttons.assigning", "Assigning...")
            : t("exams.reassign.reassignButton", "Reassign")}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

ReassignAnnotatorModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  exam: PropTypes.shape({
    _row_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    exam_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    annotator1: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      status: PropTypes.string,
    }),
    annotator2: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      status: PropTypes.string,
    }),
  }),
  annotators: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
    })
  ).isRequired,
  userInfo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

ReassignAnnotatorModal.defaultProps = {
  exam: null,
  userInfo: null,
  onSuccess: null,
  onError: null,
};
